import {AbstractContainer} from "./AbstractContainer";
import {dateFromStr, dateFromYearMonthDay, formatDate, TimeCalculator} from "../time/TimeCalculator";
import {PeriodicityEnum} from "../enum/enum";
import * as $ from "jquery";
import {Slider} from "../ui/Slider";
import '/modules/common/css/timeseries/datepicker.css';
import '/modules/common/css/timeseries/datepicker.responsive.css';

/**
 * A dates picker from until
 * @extends AbstractContainer
 */
class DatePicker extends AbstractContainer {

    getClassName(){
        return "DatePicker";
    }

    static MAX_DAYS = 15 * 365;

    /**
     *
     * @param id {string} The HTML id for this container
     * @param translator {Translator} A translator
     * @param layers {Array<Layer>}
     * @param periodicity {PeriodicityEnum}
     * @param runtimeConfig {RuntimeConfig} The runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     **/
    constructor(id, translator, layers, periodicity = PeriodicityEnum.DAILY, runtimeConfig, onChangeRuntimeConfig, isMobileDevice) {
        super(id, translator, isMobileDevice, runtimeConfig);
        this.onChangeRuntimeConfig = onChangeRuntimeConfig;
        this.layers = layers;
        this.onChangeCallbacks = new Set();
        const maxHardUntil = this._getHardMaxUntil();
        this.timeCalculator = new TimeCalculator(
            periodicity,
            formatDate(this._getHardMinFrom()),
            formatDate(maxHardUntil),
            this.translator
        );
        this.setDefaultFromUntil(this.timeCalculator.addDays(maxHardUntil, -365), maxHardUntil);
    }

    onChange(callback) {
        if(callback)
            this.onChangeCallbacks.add(callback);
        return this;
    }

    _getMinUntil() {
        return this.timeCalculator.addDays(
            this.timeCalculator.min(this._getMaxFrom(), dateFromStr(this.getFrom())),
            1
        );
    }

    _getHardMaxUntil() {
        const manyLayers = this.layers.length > 1;
        return this.layers
            .map(l => {
                const tc = l.getTimeCalculator();
                const dataUntil = l.getDataUntil();
                const year = tc.getYearFromDate(dataUntil);
                const month = tc.getMonthFromDate(dataUntil);
                const day = !manyLayers
                    ? tc.getDayFromDate(tc.getUntil())
                    : tc.getUnboundedMaxDayForYearMonth(year, month);
                return dateFromYearMonthDay(year, month, day);
            }).sort((d1, d2) => d2 - d1)[0];
    }

    _getHardMinFrom() {
        return this.layers
            .map(l => l.getDataFrom())
            .sort((d1, d2) => d1 - d2)[0];
    }

    _getMaxFrom() {
        return this.timeCalculator.addDays(
            this.timeCalculator.max(this._getHardMaxUntil(), dateFromStr(this.getUntil())),
            -1
        );
    }

    getFrom() {
        const from = this.runtimeConfig.getToolProperty(this.id, 'timeFrom');
        return from ? from : formatDate(this._getHardMinFrom());
    }

    /**
     * Set default dates
     * @param from {Date}
     * @param until {Date}
     */
    setDefaultFromUntil(from, until) {
        if (!this.runtimeConfig.getToolProperty(this.id, 'timeFrom'))
            this.runtimeConfig.setToolProperty(this.id, 'timeFrom', formatDate(from));
        if (!this.runtimeConfig.getToolProperty(this.id, 'timeUntil'))
            this.runtimeConfig.setToolProperty(this.id, 'timeUntil', formatDate(until));
    }

    _getThirdComponentFromDate(date) {
        switch (this.timeCalculator.getPeriodicity()) {
            case PeriodicityEnum.CUSTOM:
            case PeriodicityEnum.YEARLY:
            case PeriodicityEnum.MONTHLY_FORECAST:
            case PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY:
            case PeriodicityEnum.MONTHLY:
            case PeriodicityEnum.WEEKLY:
            case PeriodicityEnum.ISO_WEEKLY:
                return undefined;
            case PeriodicityEnum.DAILY:
                return this.timeCalculator.getDayFromDate(date);
            case PeriodicityEnum.TEN_DAILY:
                return this.timeCalculator.getTenDaysFromDate(date);
            default:
                return undefined;
        }
    }

    _getSecondComponentFromDate(date) {
        switch (this.timeCalculator.getPeriodicity()) {
            case PeriodicityEnum.CUSTOM:
            case PeriodicityEnum.YEARLY:
                return undefined;
            case PeriodicityEnum.WEEKLY:
                return this.timeCalculator.getWeekFromDate(date);
            case PeriodicityEnum.ISO_WEEKLY:
                return this.timeCalculator.getISOWeekFromDate(date);
            case PeriodicityEnum.MONTHLY_FORECAST:
            case PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY:
            case PeriodicityEnum.MONTHLY:
            case PeriodicityEnum.DAILY:
            case PeriodicityEnum.TEN_DAILY:
                return this.timeCalculator.getMonthFromDate(date);
            case PeriodicityEnum.TEN_DAILY_YEARLESS:
            case PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY:
                return this.timeCalculator.getTenDaysFromDate(date);
            case PeriodicityEnum.DAILY_YEARLESS:
                return this.timeCalculator.getDayFromDate(date);
            default:
                return undefined;
        }
    }

    _getFirstComponentFromDate(date) {
        switch (this.timeCalculator.getPeriodicity()) {
            case PeriodicityEnum.TEN_DAILY_YEARLESS:
            case PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY:
            case PeriodicityEnum.DAILY_YEARLESS:
                return this.timeCalculator.getMonthFromDate(date);
            default:
                return this.timeCalculator.getYearFromDate(date);
        }
    }

    _dateFromComponents(firstComponent, secondComponent, thirdComponent = undefined) {
        switch (this.timeCalculator.getPeriodicity()) {
            case PeriodicityEnum.CUSTOM:
                return undefined;
            case PeriodicityEnum.YEARLY:
                return dateFromStr(this.timeCalculator.writeYearly(firstComponent));
            case PeriodicityEnum.WEEKLY:
                return dateFromStr(this.timeCalculator.writeWeekly(firstComponent, secondComponent));
            case PeriodicityEnum.ISO_WEEKLY:
                return dateFromStr(this.timeCalculator.writeISOWeekly(firstComponent, secondComponent));
            case PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY:
            case PeriodicityEnum.MONTHLY:
                return dateFromStr(this.timeCalculator.writeMonthly(firstComponent, secondComponent));
            case PeriodicityEnum.MONTHLY_FORECAST:
                return dateFromStr(this.timeCalculator.writeMonthly(firstComponent, secondComponent));
            case PeriodicityEnum.DAILY:
            case PeriodicityEnum.DAILY_YEARLESS:
                return dateFromStr(this.timeCalculator.writeDaily(firstComponent, secondComponent, thirdComponent));
            case PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY:
            case PeriodicityEnum.TEN_DAILY_YEARLESS:
            case PeriodicityEnum.TEN_DAILY:
                return dateFromStr(this.timeCalculator.writeTenDaily(firstComponent, secondComponent, thirdComponent));
            default:
                return undefined;
        }
    }

    _setFrom(firstComponent, secondComponent, thirdComponent = undefined) {
        const from = this.getFrom();
        const fromDate = dateFromStr(from);
        firstComponent = firstComponent !== undefined ? firstComponent : this._getFirstComponentFromDate(fromDate);
        secondComponent = secondComponent !== undefined ? secondComponent : this._getSecondComponentFromDate(fromDate);
        thirdComponent = thirdComponent !== undefined ? thirdComponent : this._getThirdComponentFromDate(fromDate);
        const dateFromSliders = this.timeCalculator.isYearless()
            ? this._dateFromComponents(0, firstComponent, secondComponent)
            : this._dateFromComponents(firstComponent, secondComponent, thirdComponent);
        if (from !== formatDate(dateFromSliders)) {
            this.runtimeConfig.setToolProperty(this.id, 'timeFrom', formatDate(dateFromSliders));
            this.onChangeRuntimeConfig();
            if (this.timeCalculator.getDifferenceInDays(dateFromStr(this.getUntil()), dateFromSliders) > DatePicker.MAX_DAYS) {
                const fixedUntil = this.timeCalculator.min(
                    this._getHardMaxUntil(),
                    this.timeCalculator.addDays(dateFromSliders, DatePicker.MAX_DAYS)
                );
                this._setUntil(this.timeCalculator.getYearFromDate(fixedUntil), this.timeCalculator.getMonthFromDate(fixedUntil), this.timeCalculator.getDayFromDate(fixedUntil));
            } else {
                this.onChangeCallbacks.forEach(c => c(this));
            }
        }
    }

    getUntil() {
        const until = this.runtimeConfig.getToolProperty(this.id, 'timeUntil');
        return until ? until : formatDate(this._getTimeCalculator(false).getUntil());
    }

    getUntilPeriodStart() {
        return this.timeCalculator.fixDate(dateFromStr(this.getUntil()));
    }

    _setUntil(firstComponent, secondComponent, thirdComponent) {
        const until = this.getUntil();
        const untilDate = dateFromStr(until);
        firstComponent = firstComponent !== undefined ? firstComponent : this._getFirstComponentFromDate(untilDate);
        secondComponent = secondComponent !== undefined ? secondComponent : this._getSecondComponentFromDate(untilDate);
        thirdComponent = thirdComponent !== undefined ? thirdComponent : this._getThirdComponentFromDate(untilDate);
        const dateFromSliders = this.timeCalculator.isDaily() || this.timeCalculator.isDailyYearless()
            ? dateFromYearMonthDay(firstComponent, secondComponent, thirdComponent)
            : this.timeCalculator.isTenDaily() || this.timeCalculator.isTenDailyYearless()
                ? dateFromStr(this.timeCalculator.writeTenDaily(firstComponent, secondComponent, thirdComponent))
                : this.timeCalculator.isMonthlyUpdatedTenDaily() || this.timeCalculator.isTenDailyYearless30Day()
                    ? dateFromStr(this.timeCalculator.writeMonthlyUpdatedTenDaily(firstComponent, secondComponent))
                    : this.timeCalculator.getLastDayOfMonthFromDate(this._dateFromComponents(firstComponent, secondComponent, thirdComponent));
        if (until !== formatDate(dateFromSliders)) {
            this.runtimeConfig.setToolProperty(this.id, 'timeUntil', formatDate(dateFromSliders));
            this.onChangeRuntimeConfig();
            if (this.timeCalculator.getDifferenceInDays(dateFromSliders, dateFromStr(this.getFrom())) > DatePicker.MAX_DAYS) {
                const fixedFrom = this.timeCalculator.max(
                    this._getHardMinFrom(),
                    this.timeCalculator.addDays(dateFromSliders, -DatePicker.MAX_DAYS)
                );
                this._setFrom(this.timeCalculator.getYearFromDate(fixedFrom), this.timeCalculator.getMonthFromDate(fixedFrom), this.timeCalculator.getDayFromDate(fixedFrom));
            } else {
                this.onChangeCallbacks.forEach(c => c(this));
            }
        }
    }

    isExpanded() {
        return this.getSessionProp("expanded") === true;
    }

    getContainerElement() {
        const expanded = this.isExpanded();
        const container = $(
            '<div class="date-picker container ' + (expanded ? 'expanded' : '') + '">' +
            '   <div class="header" role="heading">' +
            '       <div class="title">' +
            '           <h3></h3>' +
            '       </div>' +
            '       <div class="buttons-container">' +
            '           <button class="expander ' + (expanded ? 'collapse' : 'expand') + '" type="button" title="' + this.translator.translate('tool.charts.expander') + '"></button>' +
            '       </div>' +
            '   </div>' +
            '   <div class="body" aria-expanded="' + expanded + '">' +
            '       <div class="from title">' + this.translator.translate('tool.charts.from.title') + '</div>' +
            '       <div class="sliders-container from-container one"><div class="first-container"></div></div>' +
            '       <div class="until title">' + this.translator.translate('tool.charts.until.title') + '</div>' +
            '       <div class="sliders-container until-container one"><div class="first-container"></div></div>' +
            '   </div>' +
            '</div>'
        );
        if (!this.timeCalculator.isYearly() && !this.timeCalculator.isTenDailyYearless30Day()) {
            $('.sliders-container', container)
                .append($('<div class="second-container"></div>'))
                .removeClass('one')
                .addClass('two');
        }
        if (!this.timeCalculator.isYearly() && !this.timeCalculator.isMonthly() && !this.timeCalculator.isMonthlyForecast()
            && !this.timeCalculator.isMonthlyUpdatedTenDaily() && !this.timeCalculator.isWeekly() && !this.timeCalculator.isISOWeekly() && !this.timeCalculator.isYearless()) {
            $('.sliders-container', container)
                .append($('<div class="third-container"></div>'))
                .removeClass('two')
                .addClass('three');
        }

        const onChangePicker = picker => {
            const pickedFromStr = picker.getFrom();
            const pickedUntilStr = formatDate(this.timeCalculator.isMonthlyUpdatedTenDaily() ? picker.getUntil() : this.timeCalculator.getEndDateOfPeriod(picker.getUntil()));
            $('.header .title h3', container).html(picker.translator.translate('tool.charts.time.title', [pickedFromStr, pickedUntilStr]))
        };
        onChangePicker(this);
        this.onChange(onChangePicker);

        return this._createTimePickers(container);
    }

    _createTimePickers(container) {
        this.firstComponentSliders = [];
        this.secondComponentSliders = [];
        this.thirdComponentSliders = [];

        const self = this;

        function createTimeSliders(from) {
            const createFirstComponentSlider = from => { //can be year or month
                const timeCalculator = self._getTimeCalculator(from);
                const sliderIndex = from ? 0 : 1;
                const sliderId = self.id + '_' + (from ? 'from_firstComponent' : 'until_firstComponent');
                const pickedFromStr = self.getFrom();
                const pickedFrom = dateFromStr(pickedFromStr);
                const pickedUntilStr = self.getUntil();
                const pickedUntil = dateFromStr(pickedUntilStr);
                const picked = from ? pickedFrom : pickedUntil;
                const isMonth = self.timeCalculator.isYearless();
                const min = isMonth ? timeCalculator.getMinMonthForYear() : timeCalculator.getMinYear();
                const max = isMonth ? timeCalculator.getMaxMonthForYear() : timeCalculator.getMaxYear();
                self.firstComponentSliders[sliderIndex] = new Slider(
                    self.translator,
                    self.isMobileDevice,
                    sliderId,
                    min, max, isMonth?timeCalculator.getMonthFromDate(picked):timeCalculator.getYearFromDate(picked), 1, max - min + 1,
                    undefined, undefined,
                    function (slider) {
                        const secondComponent = self.secondComponentSliders[slider.get('index')]
                            ? self.secondComponentSliders[slider.get('index')].getValue()
                            : undefined;
                        const thirdComponent = self.thirdComponentSliders[slider.get('index')]
                            ? self.thirdComponentSliders[slider.get('index')].getValue()
                            : undefined;
                        if (slider.get('from'))
                            self._setFrom(slider.getValue(), secondComponent, thirdComponent);
                        else self._setUntil(slider.getValue(), secondComponent, thirdComponent);
                    }, true, true, undefined, undefined, undefined
                ).set('index', sliderIndex).set('from', from);
                const yearContainer = $(
                    '<div class="slider-container time-container first ' + (from ? 'from' : 'until') + '">' +
                    '   <label for="' + sliderId + '">' + self.translator.translate('tool.charts.' + (from ? 'from' : 'until') + '.' + (isMonth ? 'monthLabel' : 'yearLabel')) + '</label>' +
                    '</div>'
                ).append(self.firstComponentSliders[sliderIndex].getUIElement());
                $(from ? '.from-container .first-container' : '.until-container .first-container', container)
                    .empty().append(yearContainer);
            };

            createFirstComponentSlider(from);

            const createSecondComponentSlider = from => {
                const timeCalculator = self._getTimeCalculator(from);
                const sliderIndex = from ? 0 : 1;
                const sliderId = self.id + '_' + (from ? 'from_secondComponent' : 'until_secondComponent');
                const picked = from ? dateFromStr(self.getFrom()) : dateFromStr(self.getUntil());
                const year = timeCalculator.getYearFromDate(picked);
                const maxSecond = parentValue => self.timeCalculator.isWeekly()
                    ? timeCalculator.getMaxWeekForYear(parentValue)
                    : self.timeCalculator.isISOWeekly()
                        ? timeCalculator.getMaxWeekForYear(parentValue)
                        : timeCalculator.getMaxMonthForYear(parentValue);
                const minSecond = parentValue => self.timeCalculator.isWeekly()
                    ? timeCalculator.getMinWeekForYear(parentValue)
                    : self.timeCalculator.isISOWeekly()
                        ? timeCalculator.getMinWeekForYear(parentValue)
                        : timeCalculator.getMinMonthForYear(parentValue);
                const min = minSecond(year);
                const max = maxSecond(year);
                const value = self.timeCalculator.isWeekly()
                    ? timeCalculator.getWeekFromDate(picked)
                    : self.timeCalculator.isISOWeekly()
                        ? timeCalculator.getISOWeekFromDate(picked)
                        : timeCalculator.getMonthFromDate(picked);
                self.secondComponentSliders[sliderIndex] = new Slider(
                    self.translator,
                    self.isMobileDevice,
                    sliderId,
                    min, max, value, 1, max - min + 1,
                    undefined, undefined,
                    function (slider) {
                        const thirdComponent = self.thirdComponentSliders[slider.get('index')]
                            ? self.thirdComponentSliders[slider.get('index')].getValue()
                            : undefined;
                        if (slider.get('from'))
                            self._setFrom(self.firstComponentSliders[slider.get('index')].getValue(), slider.getValue(), thirdComponent);
                        else self._setUntil(self.firstComponentSliders[slider.get('index')].getValue(), slider.getValue(), thirdComponent);
                    }, true, true, self.firstComponentSliders[sliderIndex],
                    maxSecond, minSecond
                ).set('index', sliderIndex).set('from', from);
                const secondComponentLabelKey = self.timeCalculator.isWeekly() ? 'weekLabel' : self.timeCalculator.isISOWeekly() ? 'isoWeekLabel' : 'monthLabel';
                const labelKey = 'tool.charts.' + (from ? 'from' : 'until') + '.' + secondComponentLabelKey;
                const secondContainer = $(
                    '<div class="slider-container time-container second ' + (from ? 'from' : 'until') + '">' +
                    '   <label for="' + sliderId + '">' + self.translator.translate(labelKey) + '</label>' +
                    '</div>'
                ).append(self.secondComponentSliders[sliderIndex].getUIElement());
                $(from ? '.from-container .second-container' : '.until-container .second-container', container)
                    .empty().append(secondContainer);
            };

            if (!self.timeCalculator.isYearly() && !self.timeCalculator.isTenDailyYearless30Day())
                createSecondComponentSlider(from);

            function createThirdComponentSlider(from) {
                const timeCalculator = self._getTimeCalculator(from);
                const sliderIndex = from ? 0 : 1;
                const sliderId = self.id + '_' + (from ? 'from_thirdComponent' : 'until_thirdComponent');
                const picked = from ? dateFromStr(self.getFrom()) : dateFromStr(self.getUntil());
                const year = timeCalculator.getYearFromDate(picked);
                const month = timeCalculator.getMonthFromDate(picked);
                const min = timeCalculator.isTenDaily() ? timeCalculator.getMinTenDaysForYearMonth(year, month) : timeCalculator.getMinDayForYearMonth(year, month);
                const max = timeCalculator.isTenDaily()
                    ? timeCalculator.getMaxTenDaysForYearMonth(year, month)
                    : timeCalculator.isDaily()
                        ? timeCalculator.getDayFromDate(timeCalculator.min(dateFromYearMonthDay(year, month, timeCalculator.getMaxDayForYearMonth(year, month)), timeCalculator.getUntil()))
                        : timeCalculator.getMaxDayForYearMonth(year, month);
                self.thirdComponentSliders[sliderIndex] = new Slider(
                    self.translator,
                    self.isMobileDevice,
                    sliderId,
                    min, max, self.timeCalculator.isTenDaily() ? timeCalculator.getTenDaysFromDate(picked) : timeCalculator.getDayFromDate(picked), 1, max - min + 1,
                    undefined, undefined,
                    function (slider) {
                        if (slider.get('from'))
                            self._setFrom(self.firstComponentSliders[slider.get('index')].getValue(), self.secondComponentSliders[slider.get('index')].getValue(), slider.getValue());
                        else self._setUntil(self.firstComponentSliders[slider.get('index')].getValue(), self.secondComponentSliders[slider.get('index')].getValue(), slider.getValue());
                    }, true, true, self.secondComponentSliders[sliderIndex],
                    function (parentValue) {
                        return self.timeCalculator.isTenDaily()
                            ? timeCalculator.getMaxTenDaysForYearMonth(self.firstComponentSliders[this.get('index')].getValue(), parentValue)
                            : timeCalculator.getMaxDayForYearMonth(self.firstComponentSliders[this.get('index')].getValue(), parentValue);
                    },
                    function (parentValue) {
                        return self.timeCalculator.isTenDaily()
                            ? timeCalculator.getMinTenDaysForYearMonth(self.firstComponentSliders[this.get('index')].getValue(), parentValue)
                            : timeCalculator.getMinDayForYearMonth(self.firstComponentSliders[this.get('index')].getValue(), parentValue);
                    }
                ).set('index', sliderIndex).set('from', from);
                const thirdComponentLabelKey = self.timeCalculator.isTenDaily() || self.timeCalculator.isTenDailyYearless() ? 'tenDayLabel' : 'dayLabel';
                const labelKey = 'tool.charts.' + (from ? 'from' : 'until') + '.' + thirdComponentLabelKey;
                const thirdContainer = $(
                    '<div class="slider-container time-container third ' + (from ? 'from' : 'until') + '">' +
                    '   <label for="' + sliderId + '">' + self.translator.translate(labelKey) + '</label>' +
                    '</div>'
                ).append(self.thirdComponentSliders[sliderIndex].getUIElement());
                $(from ? '.from-container .third-container' : '.until-container .third-container', container)
                    .empty().append(thirdContainer);
            }

            if (!self.timeCalculator.isYearly() && !self.timeCalculator.isTenDailyYearless30Day() && !self.timeCalculator.isMonthly()
                && !self.timeCalculator.isMonthlyForecast() && !self.timeCalculator.isMonthlyUpdatedTenDaily() && !self.timeCalculator.isWeekly() && !self.timeCalculator.isISOWeekly())
                createThirdComponentSlider(from);

            const sliderIndex = from ? 0 : 1;
            if (from) {
                self._setFrom(
                    self.firstComponentSliders[sliderIndex].getValue(),
                    self.secondComponentSliders[sliderIndex] ? self.secondComponentSliders[sliderIndex].getValue() : undefined,
                    self.thirdComponentSliders[sliderIndex] ? self.thirdComponentSliders[sliderIndex].getValue() : undefined
                );
            } else {
                self._setUntil(
                    self.firstComponentSliders[sliderIndex].getValue(),
                    self.secondComponentSliders[sliderIndex] ? self.secondComponentSliders[sliderIndex].getValue() : undefined,
                    self.thirdComponentSliders[sliderIndex] ? self.thirdComponentSliders[sliderIndex].getValue() : undefined
                );
            }

            const onChange = () => {
                createTimeSliders(from);
                createTimeSliders(!from);
            };
            self.firstComponentSliders[sliderIndex].onChange(onChange);
            if (self.secondComponentSliders[sliderIndex])
                self.secondComponentSliders[sliderIndex].onChange(onChange);
            if (self.thirdComponentSliders[sliderIndex])
                self.thirdComponentSliders[sliderIndex].onChange(onChange);

        }

        createTimeSliders(true);
        createTimeSliders(false);
        return container;
    }

    _getTimeCalculator(from) {
        return new TimeCalculator(
            this.timeCalculator.getPeriodicity(),
            formatDate(from ? this._getHardMinFrom() : this._getMinUntil()),
            formatDate(from ? this._getMaxFrom() : this._getHardMaxUntil()),
            this.translator
        );
    }
}

export {DatePicker}